import canvaskitWasmUrl from 'canvaskit-wasm/bin/full/canvaskit.wasm?url';
import CanvasKitInit, { type FontMgr, type CanvasKit } from 'canvaskit-wasm/full';
import {
  EMOJI_FONT_FAMILY,
  type FontVariantSet,
  EMOJI_FONT_FACE_CSS,
  SYMBOL_FONT_FAMILY,
  SYMBOL_FONT_FACE_CSS,
  degradeGoogleFontUrl,
  googleFontVariantUrls,
  primaryFontFamily,
} from '@nexmail/shared-util';

let ckPromise: Promise<CanvasKit> | null = null;

// Current fonts CSS or import URL (set by the app when template context changes)
let currentFontsCss: string | undefined;

// Subscribers notified whenever currentFontsCss changes, so live CanvasKit
// surfaces (the editor) can rebuild their FontMgr when fonts load/change rather
// than only reading the value once at init.
const fontsSubscribers = new Set<() => void>();

/** Subscribe to currentFontsCss changes. Returns an unsubscribe fn. */
export function subscribeFonts(cb: () => void): () => void {
  fontsSubscribers.add(cb);
  return () => fontsSubscribers.delete(cb);
}

/** Current fonts CSS/URL snapshot (for useSyncExternalStore). */
export function getCurrentFontsCss(): string | undefined {
  return currentFontsCss;
}

function setCurrentFontsCss(value: string | undefined) {
  if (currentFontsCss === value) return;
  currentFontsCss = value;
  syncDomFontFaces(value);
  fontsSubscribers.forEach((cb) => cb());
}

// CanvasKit renders canvas text from its own FontMgr, but DOM overlays -- most
// importantly the inline text editor's contentEditable -- render through the
// browser's font engine. Mirror the same font CSS into a managed <style> so
// those surfaces resolve the design's `fontFamily` to the real face instead of
// falling back to a serif (whose wider/taller metrics push edited text outside
// the node's box). The browser renders the css2 woff2 faces fine -- only the
// FontMgr needs the Fontsource substitution -- so the @import that drives the
// FontMgr drives the DOM too.
let domFontStyleEl: HTMLStyleElement | null = null;
function syncDomFontFaces(cssOrUrl: string | undefined): void {
  if (typeof document === 'undefined') return;
  // A bare legacy Google Fonts URL (no @import/@font-face) becomes an @import.
  const css =
    cssOrUrl && !cssOrUrl.includes('@import') && !cssOrUrl.includes('@font-face')
      ? `@import url('${cssOrUrl}');`
      : cssOrUrl;
  if (!css) {
    domFontStyleEl?.remove();
    domFontStyleEl = null;
    return;
  }
  if (!domFontStyleEl) {
    domFontStyleEl = document.createElement('style');
    domFontStyleEl.setAttribute('data-canvas-editor-fonts', '');
    document.head.appendChild(domFontStyleEl);
  }
  if (domFontStyleEl.textContent !== css) domFontStyleEl.textContent = css;
}

// Cache: fontsCss (or 'default') -> FontMgr promise
const fontMgrCache = new Map<string, Promise<FontMgr>>();

// Cache: font file URL -> ArrayBuffer
const fontFileCache = new Map<string, ArrayBuffer>();

// ── On-demand font variant layer ────────────────────────────────────────
// The app sets a `base` fonts CSS (brand fonts + the committed document's
// variants). When the *live* scene starts using a variant that base hasn't
// loaded yet -- a freshly toggled italic, a newly picked weight -- the editor
// reports it via ensureFontVariantsLoaded(); we append only the genuinely-new
// css2 @imports on top of the base and recompose currentFontsCss, which drives
// the existing FontMgr-rebuild + redraw path. Base is never mutated, so load
// behaviour is identical until an on-demand variant actually appears.
let baseFontsCss: string | undefined;
const extraVariants: FontVariantSet = {};

const unionSorted = (a: number[], b: number[]): number[] =>
  [...new Set([...a, ...b])].sort((x, y) => x - y);

/** Families declared via @font-face in the base (uploaded brand fonts, emoji):
 *  these have no fetchable-by-name css2 face, so on-demand must skip them. */
function selfHostedFamilies(): Set<string> {
  const set = new Set<string>();
  const blocks = (baseFontsCss ?? '').match(/@font-face\s*\{[^}]*\}/gi) ?? [];
  for (const block of blocks) {
    const m = block.match(/font-family:\s*['"]?([^'";]+)['"]?/i);
    if (m) set.add(m[1].trim());
  }
  return set;
}

function recomposeFontsCss() {
  const extraUrls =
    Object.keys(extraVariants).length > 0 ? googleFontVariantUrls(extraVariants) : [];
  const baseStr = baseFontsCss ?? '';
  // Only add imports the base doesn't already carry (dedupe by exact URL).
  const newImports = extraUrls
    .filter((u) => !baseStr.includes(u))
    .map((u) => `@import url('${u}');`);
  if (newImports.length === 0) {
    setCurrentFontsCss(baseFontsCss);
    return;
  }
  // Normalize a bare legacy Google URL base into an @import so it composes
  // with the on-demand lines; @import/@font-face CSS passes through unchanged.
  const basePart =
    baseStr && !baseStr.includes('@import') && !baseStr.includes('@font-face')
      ? `@import url('${baseStr}');`
      : baseStr;
  setCurrentFontsCss([basePart, ...newImports].filter(Boolean).join('\n'));
}

/**
 * Load any font variants the live scene needs that aren't loaded yet. Safe to
 * call on every scene change: it no-ops unless a new (family, weight, italic)
 * tuple appears, and skips self-hosted (@font-face) families that can't be
 * fetched by name. Triggers a FontMgr rebuild + redraw via currentFontsCss.
 */
export function ensureFontVariantsLoaded(variants: FontVariantSet): void {
  const selfHosted = selfHostedFamilies();
  let changed = false;
  for (const [family, axes] of Object.entries(variants)) {
    if (
      !family ||
      family === EMOJI_FONT_FAMILY ||
      family === SYMBOL_FONT_FAMILY ||
      selfHosted.has(family)
    )
      continue;
    const cur = extraVariants[family] ?? { weights: [], italicWeights: [] };
    const nextWeights = unionSorted(cur.weights, axes.weights ?? []);
    const nextItalic = unionSorted(cur.italicWeights, axes.italicWeights ?? []);
    if (
      !extraVariants[family] ||
      nextWeights.length !== cur.weights.length ||
      nextItalic.length !== cur.italicWeights.length
    ) {
      extraVariants[family] = { weights: nextWeights, italicWeights: nextItalic };
      changed = true;
    }
  }
  if (changed) recomposeFontsCss();
}

export function getCanvasKit(): Promise<CanvasKit> {
  if (!ckPromise) {
    ckPromise = CanvasKitInit({
      locateFile: () => canvaskitWasmUrl,
    });
    // A failed wasm fetch (flaky network, mid-deploy 404) must not be cached
    // forever -- clearing the promise lets the next caller retry instead of
    // wedging the editor until a full page reload.
    ckPromise.catch(() => {
      ckPromise = null;
    });
  }
  return ckPromise!;
}

/**
 * Set the font CSS (or legacy Google Fonts URL) for the current template context.
 * All subsequent getFontMgr() calls will include these fonts.
 * Call with undefined to reset to default (Roboto only).
 */
export function setFontsImportUrl(cssOrUrl: string | undefined) {
  baseFontsCss = cssOrUrl;
  recomposeFontsCss();
}

/**
 * Build and set Google Fonts imports from font family names.
 * Convenience wrapper for contexts that have font family names but not CSS.
 */
export function setFontFamilies(families: string[]) {
  const variants: FontVariantSet = {};
  for (const f of families) {
    // primaryFontFamily extracts the primary face from a CSS stack and returns
    // null for generics + web-safe system fonts (no Google/Fontsource face to
    // load), so a brand font labelled "Helvetica" no longer triggers a 404.
    const family = primaryFontFamily(f);
    if (family) variants[family] ??= { weights: [], italicWeights: [] };
  }
  setFontVariants(variants);
}

/**
 * Set the fonts for the current template context from the variants the design
 * actually uses, so italic/heavy faces (e.g. a Figma import's ExtraBold
 * Italic) are loaded into the FontMgr instead of being substituted by an
 * upright face with different advance widths -- a substitution that shifts
 * wrapping and stretches justified lines. One @import per family so a family
 * missing a variant on Google can't fail the whole set (css2 hard-400s the
 * entire request on any unknown tuple; see the degrade retry in
 * fetchAndExtractFontUrls).
 */
export function setFontVariants(variants: FontVariantSet, extraCss?: string) {
  const urls = googleFontVariantUrls(variants);
  const parts = urls.map((u) => `@import url('${u}');`);
  // Emoji fallback rides a direct @font-face (Google's css2 serves emoji
  // formats CanvasKit can't rasterize); extractFontFileUrls pulls the src URL.
  if (variants[EMOJI_FONT_FAMILY]) parts.push(EMOJI_FONT_FACE_CSS);
  // Symbol/dingbat fallback rides a direct @font-face the same way (its family
  // isn't css2-resolvable); extractFontFileUrls pulls the src URL.
  if (variants[SYMBOL_FONT_FAMILY]) parts.push(SYMBOL_FONT_FACE_CSS);
  // Uploaded brand fonts ride @font-face blocks the same way (a non-Google
  // family can't be fetched by name; extractFontFileUrls pulls the src URL).
  if (extraCss) parts.push(extraCss);
  baseFontsCss = parts.length === 0 ? undefined : parts.join('\n');
  recomposeFontsCss();
}

/**
 * Get a FontMgr with bundled Roboto + any fonts from the current CSS.
 * Results are cached by CSS string.
 */
export function getFontMgr(): Promise<FontMgr> {
  const cacheKey = currentFontsCss || 'default';

  if (fontMgrCache.has(cacheKey)) return fontMgrCache.get(cacheKey)!;

  const promise = buildFontMgr(currentFontsCss);
  fontMgrCache.set(cacheKey, promise);
  return promise;
}

/**
 * Get a FontMgr for a specific font CSS string (or legacy URL).
 * Unlike getFontMgr(), this does not depend on the module-level global,
 * so it's safe to call from components that receive the value as a prop.
 */
export function getFontMgrForUrl(fontsCssOrUrl: string | undefined): Promise<FontMgr> {
  const cacheKey = fontsCssOrUrl || 'default';
  if (fontMgrCache.has(cacheKey)) return fontMgrCache.get(cacheKey)!;

  const promise = buildFontMgr(fontsCssOrUrl);
  fontMgrCache.set(cacheKey, promise);
  return promise;
}

async function buildFontMgr(fontsCssOrUrl?: string): Promise<FontMgr> {
  const ck = await getCanvasKit();

  // Always load bundled Roboto
  const bundledPromises = [
    fetch('/fonts/Roboto-Regular.ttf').then((r) => r.arrayBuffer()),
    fetch('/fonts/Roboto-Bold.ttf').then((r) => r.arrayBuffer()),
  ];

  // Extract font file URLs from CSS or legacy URL
  let extraPromises: Promise<ArrayBuffer | null>[] = [];
  if (fontsCssOrUrl) {
    try {
      const fontFileUrls = await extractFontFileUrls(fontsCssOrUrl);
      extraPromises = fontFileUrls.map((url) => fetchFontFile(url));
    } catch (err) {
      console.warn('[CanvasKit] Failed to extract font URLs:', err);
    }
  }

  const [regular, bold, ...extras] = await Promise.all([...bundledPromises, ...extraPromises]);
  const allBuffers = [regular, bold, ...extras.filter(Boolean)] as ArrayBuffer[];

  const mgr = ck.FontMgr.FromData(...allBuffers);
  if (!mgr) throw new Error('Failed to create FontMgr');
  return mgr;
}

// ── Font URL extraction ─────────────────────────────────────────────────

// Cache: input -> extracted font file URLs
const extractionCache = new Map<string, string[]>();

/**
 * Extract downloadable font file URLs from a CSS string or bare URL.
 *
 * Handles:
 * - Bare Google Fonts URL (legacy): fetch CSS, extract .woff2/.ttf URLs
 * - CSS with @import url(...): fetch each import URL's CSS, extract font files
 * - CSS with @font-face { src: url(...) }: extract src URLs directly
 */
async function extractFontFileUrls(cssOrUrl: string): Promise<string[]> {
  if (extractionCache.has(cssOrUrl)) return extractionCache.get(cssOrUrl)!;

  const urls: string[] = [];

  // Check if it's a bare URL (no CSS directives)
  const isCss = cssOrUrl.includes('@import') || cssOrUrl.includes('@font-face');

  if (!isCss) {
    // Legacy: bare Google Fonts URL — fetch its CSS and extract font files
    const fontUrls = await fetchAndExtractFontUrls(cssOrUrl);
    urls.push(...fontUrls);
  } else {
    // Extract @import URLs and fetch their CSS for font file URLs
    const importUrls = [...cssOrUrl.matchAll(/@import\s+url\(['"]?([^'")\s]+)['"]?\)/g)].map(
      (m) => m[1]
    );
    for (const importUrl of importUrls) {
      const fontUrls = await fetchAndExtractFontUrls(importUrl);
      urls.push(...fontUrls);
    }

    // Extract direct font file URLs from @font-face src declarations
    const srcUrls = [...cssOrUrl.matchAll(/src:\s*url\(['"]?([^'")\s]+)['"]?\)/g)].map((m) => m[1]);
    urls.push(...srcUrls);
  }

  const unique = [...new Set(urls)];
  extractionCache.set(cssOrUrl, unique);
  return unique;
}

// ── Fontsource substitution for Google css2 ─────────────────────────────
// The browser cannot spoof its User-Agent, and Google css2 now serves modern
// browsers woff2-only. This CanvasKit build PARSES woff2 (families enumerate,
// advances measure) but cannot rasterize its glyph data -- every glyph draws
// as tofu, so paragraph fallback silently re-renders Google-font text in
// bundled Roboto (wrong widths/weights; justified text inherits the width
// error as oversized word gaps). Fontsource mirrors Google Fonts as
// per-weight woff (v1, zlib) files that FreeType decodes natively, served
// with CORS -- so for css2 URLs we derive the file list straight from the
// requested (family, weight, ital) tuples and skip Google's CSS entirely.
// Families absent from Fontsource 404 and fall back to Roboto, which is the
// same place woff2 left them.

const FONTSOURCE_CDN = 'https://cdn.jsdelivr.net/fontsource/fonts';
const FONTSOURCE_SUBSETS = ['latin', 'latin-ext'];

/**
 * Fontsource package slug for a family name. Doubles as the `id` in the
 * Fontsource catalog API, so anything that wants to know whether a family will
 * actually load must key off this exact rule -- see lib/font-catalog.ts.
 */
export function fontsourceSlug(family: string): string {
  return family.trim().toLowerCase().replace(/\s+/g, '-');
}

/** Subsets the canvas asks for; a family carrying neither will 404 to Roboto. */
export const FONTSOURCE_CANVAS_SUBSETS: readonly string[] = FONTSOURCE_SUBSETS;

function fontsourceFileUrls(family: string, weight: number, italic: boolean): string[] {
  const slug = fontsourceSlug(family);
  const style = italic ? 'italic' : 'normal';
  return FONTSOURCE_SUBSETS.map(
    (subset) => `${FONTSOURCE_CDN}/${slug}@latest/${subset}-${weight}-${style}.woff`
  );
}

/** css2 URL -> Fontsource file URLs for its exact variant tuples, or null. */
function fontsourceUrlsFromCss2Url(cssUrl: string): string[] | null {
  const m = cssUrl.match(/fonts\.googleapis\.com\/css2\?family=([^&]+)/);
  if (!m) return null;
  const spec = decodeURIComponent(m[1]).replace(/\+/g, ' ');
  const [name, axes] = spec.split(':');
  if (!name) return null;

  const urls: string[] = [];
  const add = (w: number, italic: boolean) =>
    urls.push(...fontsourceFileUrls(name, Number.isFinite(w) && w > 0 ? w : 400, italic));

  if (!axes) {
    add(400, false);
    add(700, false);
  } else if (axes.startsWith('ital,wght@')) {
    for (const tuple of axes.slice('ital,wght@'.length).split(';')) {
      const [ital, w] = tuple.split(',');
      add(parseInt(w, 10), ital === '1');
    }
  } else if (axes.startsWith('wght@')) {
    for (const w of axes.slice('wght@'.length).split(';')) {
      add(parseInt(w, 10), false);
    }
  } else {
    return null;
  }
  return [...new Set(urls)];
}

/**
 * Resolve a fonts CSS URL to font FILE urls CanvasKit can decode.
 * Google css2 URLs map straight to Fontsource woff files (see above); other
 * URLs (legacy /css?family=, custom hosted CSS) are fetched and scraped, with
 * the css2 degrade retry preserved for axis tuples Google rejects.
 */
async function fetchAndExtractFontUrls(cssUrl: string): Promise<string[]> {
  const fontsource = fontsourceUrlsFromCss2Url(cssUrl);
  if (fontsource) return fontsource;

  try {
    const resp = await fetch(cssUrl);
    if (!resp.ok) {
      const degraded = degradeGoogleFontUrl(cssUrl);
      return degraded ? fetchAndExtractFontUrls(degraded) : [];
    }
    const css = await resp.text();

    // Prefer ttf when present; woff2 is a last resort (it parses but does not
    // rasterize in this CanvasKit build -- see the Fontsource note above).
    let urls = [...css.matchAll(/url\(([^)]+\.ttf)\)/g)].map((m) => m[1]);
    if (urls.length === 0) {
      urls = [...css.matchAll(/url\(([^)]+\.woff2)\)/g)].map((m) => m[1]);
    }
    return urls;
  } catch {
    return [];
  }
}

async function fetchFontFile(url: string): Promise<ArrayBuffer | null> {
  if (fontFileCache.has(url)) return fontFileCache.get(url)!;
  try {
    const resp = await fetch(url);
    if (!resp.ok) return null;
    const buffer = await resp.arrayBuffer();
    fontFileCache.set(url, buffer);
    return buffer;
  } catch {
    return null;
  }
}
